import React from 'react';
import styled from 'styled-components';
import { JobMatches, CandidateMatches } from '../../../interfaces';
import { StatusBadge } from '@/components/ui/table-components';
import { getStatusAccentColor } from '@/components/ui/styled-table';
import { formatMatchStatusLabel } from '@/utils/matchStatus';
import { fadeIn } from '@/components/Dashboard/animations';
import SelectMatchesSort from './SelectMatchesSort';

const MobileContainer = styled.div`
  margin-bottom: 1rem;
`;

const MatchCard = styled.div<{ $accentColor: string }>`
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-left: 4px solid ${(props) => props.$accentColor};
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 0.75rem;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04);
  transition: transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease;
  animation: ${fadeIn} 300ms ease-out backwards;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
    border-color: rgba(34, 123, 126, 0.5);
  }

  &:active {
    transform: translateY(0);
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    &:hover,
    &:active {
      transform: none;
    }
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
`;

const CardTitle = styled.div`
  font-weight: 600;
  font-size: 0.95rem;
  color: #0f172a;
  flex: 1;
  padding-right: 0.5rem;
`;

const CardSubtitle = styled.div`
  font-size: 0.85rem;
  color: #64748b;
  margin-top: 0.25rem;
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const CardRow = styled.div`
  display: flex;
  font-size: 0.85rem;
`;

const CardLabel = styled.span`
  color: #64748b;
  min-width: 90px;
  flex-shrink: 0;
`;

const CardValue = styled.span`
  color: #0f172a;
  flex: 1;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem 1rem;
  color: #64748b;
  font-size: 0.9rem;
  background: #f8f9fa;
  border-radius: 8px;
`;

interface MatchesMobileViewProps {
  matches: (JobMatches | CandidateMatches)[];
  onSelectMatch: (match: JobMatches | CandidateMatches) => void;
  matchType: 'role' | 'candidate';
  sortField?: string;
  onSortChange?: (field: string) => void;
}

/**
 * Card-based mobile view for matches table
 * Displays match information in a stacked card layout optimized for mobile
 */
const MatchesMobileView: React.FC<MatchesMobileViewProps> = ({
  matches,
  onSelectMatch,
  matchType,
  sortField = 'updatedAt',
  onSortChange,
}) => {
  if (matches.length === 0) {
    return (
      <MobileContainer>
        <EmptyState>
          {matchType === 'role' ? 'No role matches yet.' : 'No candidate matches yet.'}
        </EmptyState>
      </MobileContainer>
    );
  }

  const isJobMatch = (match: JobMatches | CandidateMatches): match is JobMatches => {
    return 'clientName' in match;
  };

  return (
    <MobileContainer>
      {onSortChange && (
        <SelectMatchesSort
          value={sortField}
          onChange={onSortChange}
          matchType={matchType}
        />
      )}
      {matches.map((match) => {
        const status = match.currentStatus || 'UNKNOWN';
        const accentColor = getStatusAccentColor(status);

        if (matchType === 'role' && isJobMatch(match)) {
          return (
            <MatchCard
              key={match.id}
              $accentColor={accentColor}
              onClick={() => onSelectMatch(match)}
            >
              <CardHeader>
                <div>
                  <CardTitle>{match.clientName || '-'}</CardTitle>
                  <CardSubtitle>{match.jobTitle || '-'}</CardSubtitle>
                </div>
                <StatusBadge status={formatMatchStatusLabel(status)} />
              </CardHeader>
              <CardBody>
                <CardRow>
                  <CardLabel>Location:</CardLabel>
                  <CardValue>{match.jobLocation?.displayName || '-'}</CardValue>
                </CardRow>
                <CardRow>
                  <CardLabel>Candidate:</CardLabel>
                  <CardValue>{match.candidateJobTitle || '-'}</CardValue>
                </CardRow>
                <CardRow>
                  <CardLabel>Cand. Loc:</CardLabel>
                  <CardValue>{match.candidateLocation?.displayName || '-'}</CardValue>
                </CardRow>
              </CardBody>
            </MatchCard>
          );
        }

        // Candidate match
        const candidateMatch = match as CandidateMatches;
        return (
          <MatchCard
            key={candidateMatch.id}
            $accentColor={accentColor}
            onClick={() => onSelectMatch(candidateMatch)}
          >
            <CardHeader>
              <div>
                <CardTitle>{candidateMatch.candidateName || '-'}</CardTitle>
                <CardSubtitle>{candidateMatch.candidateJobTitle || '-'}</CardSubtitle>
              </div>
              <StatusBadge status={formatMatchStatusLabel(status)} />
            </CardHeader>
            <CardBody>
              <CardRow>
                <CardLabel>Location:</CardLabel>
                <CardValue>{candidateMatch.candidateLocation?.displayName || '-'}</CardValue>
              </CardRow>
              <CardRow>
                <CardLabel>Role:</CardLabel>
                <CardValue>{candidateMatch.jobTitle || '-'}</CardValue>
              </CardRow>
            </CardBody>
          </MatchCard>
        );
      })}
    </MobileContainer>
  );
};

export default MatchesMobileView;
